import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/app/Layout";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import Avatar from "../components/app/Avatar";
import EmptyState from "../components/app/EmptyState";
import { Check, Clock, CreditCard, Sparkles, X, Ban, CheckCheck, MapPin, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const STATUS_META = {
  pending_payment: { bg: "Чака плащане",  en: "Awaiting payment", chip: "amber" },
  pending_cash:    { bg: "Кеш (изчаква)", en: "Cash pending",     chip: "amber" },
  payment_failed:  { bg: "Неуспешно плащане", en: "Payment failed", chip: "amber" },
  paid:            { bg: "Платено",       en: "Paid",             chip: ""      },
  confirmed:       { bg: "Потвърдено",    en: "Confirmed",        chip: ""      },
  cancelled:       { bg: "Отказано",      en: "Cancelled",        chip: "amber" },
  rejected:        { bg: "Отхвърлено",    en: "Rejected",         chip: "amber" },
  completed:       { bg: "Завършено",     en: "Completed",        chip: ""      },
};

const TABS = [
  { id: "upcoming", bg: "Предстоящи",  en: "Upcoming",  match: (b) => ["pending_payment","pending_cash","paid","confirmed"].includes(b.status) },
  { id: "history",  bg: "История",     en: "History",   match: (b) => ["completed","cancelled","rejected"].includes(b.status) },
  { id: "all",      bg: "Всички",      en: "All",       match: () => true },
];

export default function BookingsPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [success, setSuccess] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = async () => {
    try { const { data } = await api.get("/bookings"); setList(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  // Poll session_id on return from Stripe
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) return;
    let n = 0;
    const id = setInterval(async () => {
      n += 1;
      const items = await api.get("/bookings").then((r) => r.data).catch(() => []);
      const b = items.find((x) => x.session_id === sid);
      setList(items);
      if (b?.status === "paid" || b?.status === "confirmed") {
        setSuccess(b);
        clearInterval(id);
        // Clear ?session_id so refresh doesn't replay
        const next = new URLSearchParams(sp);
        next.delete("session_id");
        setSp(next, { replace: true });
      } else if (n >= 8) {
        clearInterval(id);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [sp, setSp]);

  const filtered = useMemo(() => {
    const sub = list.filter(TABS.find((x) => x.id === tab).match);
    return sub.sort((a, b) => (a.slot_at || "").localeCompare(b.slot_at || ""));
  }, [list, tab]);

  const action = async (b, kind) => {
    let body;
    try {
      if (kind === "confirm")   await api.post(`/bookings/${b.id}/confirm`);
      if (kind === "complete")  await api.post(`/bookings/${b.id}/complete`);
      if (kind === "cancel") {
        const hrs = (new Date(b.slot_at) - Date.now()) / 36e5;
        const isCoachSide = user?.id === b.coach_id;
        const warn = isCoachSide ? (lang === "bg" ? "Клиентът ще получи пълно възстановяване. Продължи?" : "The customer will receive a full refund. Continue?")
          : hrs >= (b.cancellation_hours ?? 24) ? (lang === "bg" ? "Ще получите автоматично пълно възстановяване. Продължи?" : "You'll get an automatic full refund. Continue?")
          : (lang === "bg" ? "По-малко от 24ч. преди тренировката — сумата НЕ се възстановява. Продължи?" : "Less than 24h before the session — this cancellation is NOT refunded. Continue?");
        if (!window.confirm(warn)) return;
        const { data } = await api.post(`/bookings/${b.id}/cancel`);
        if (data.refund_status === "refunded") toast.success(lang === "bg" ? "Сумата е възстановена" : "Refund issued");
        if (data.refund_status === "refund_failed") toast.warning(lang === "bg" ? "Възстановяването е предадено за ръчна обработка" : "Refund sent to our team for manual processing");
      }
      if (kind === "reschedule") {
        const v = window.prompt(lang === "bg" ? "Нов час (ГГГГ-ММ-ДД ЧЧ:ММ)" : "New time (YYYY-MM-DD HH:MM)", "");
        if (!v) return;
        const dt = new Date(v.replace(" ", "T"));
        if (isNaN(dt)) { toast.error("Invalid date"); return; }
        await api.post(`/bookings/${b.id}/reschedule`, { slot_at: dt.toISOString() });
      }
      if (kind === "mark-paid") await api.post(`/bookings/${b.id}/mark-paid`);
      if (kind === "reject") {
        const reason = window.prompt(lang === "bg" ? "Причина за отказ:" : "Reason for reject:") || "";
        body = { reason };
        await api.post(`/bookings/${b.id}/reject`, body);
      }
      toast.success(lang === "bg" ? "Готово" : "Done");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const dateFmt = (iso) => iso ? new Date(iso).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

  // Counts per tab — surfaced as badges
  const counts = {
    upcoming: list.filter(TABS[0].match).length,
    history:  list.filter(TABS[1].match).length,
    all:      list.length,
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="label-mono mb-1">// {lang === "bg" ? "Резервации" : "Bookings"}</div>
          <h1 className="font-oswald uppercase text-4xl sm:text-5xl tracking-tight">{lang === "bg" ? "Моите резервации" : "My bookings"}</h1>
        </div>

        {success && (
          <div className="card-sharp p-6 md:p-8 relative overflow-hidden animate-fade-up" data-testid="booking-success-banner">
            <div className="absolute inset-0 bg-gradient-to-r from-pitch/15 via-pitch/5 to-transparent" />
            <div className="absolute inset-0 pitch-field opacity-20" />
            <div className="relative flex items-start gap-5">
              <div className="w-14 h-14 rounded-full bg-pitch text-white flex items-center justify-center flex-shrink-0 shadow-[0_0_24px_rgba(59,130,246,0.45)]">
                <CheckCheck className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-mono text-pitch mb-1">// {lang === "bg" ? "Готово" : "Success"}</div>
                <h2 className="font-oswald uppercase text-2xl sm:text-3xl tracking-tight leading-none">
                  {lang === "bg" ? "Резервацията е потвърдена" : "Booking confirmed"}
                </h2>
                <p className="text-white/65 text-sm mt-2 max-w-xl">
                  {lang === "bg"
                    ? "Платежна транзакция приета. Треньорът ще получи нотификация веднага."
                    : "Payment received. The coach has been notified and will reach out shortly."}
                </p>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] font-mono text-white/55">
                  <span><span className="text-pitch">→</span> {success.other_user?.name}</span>
                  <span><Clock className="w-3 h-3 inline -mt-0.5 mr-1 text-pitch" />{dateFmt(success.slot_at)}</span>
                  <span><span className="text-pitch">€</span>{success.amount}</span>
                </div>
              </div>
              <button onClick={() => setSuccess(null)} className="p-1.5 text-white/55 hover:text-white" data-testid="booking-success-dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-white/8 -mx-4 md:mx-0 overflow-x-auto scroll-thin">
          <div className="flex gap-1 px-4 md:px-0 min-w-max">
            {TABS.map((x) => {
              const isActive = tab === x.id;
              return (
                <button
                  key={x.id}
                  onClick={() => setTab(x.id)}
                  className={`px-4 py-2.5 text-[11px] font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${isActive ? "text-pitch border-b-2 border-pitch -mb-px" : "text-white/45 hover:text-white"}`}
                  data-testid={`bookings-tab-${x.id}`}
                >
                  {lang === "bg" ? x.bg : x.en}
                  <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${isActive ? "bg-pitch text-white" : "bg-white/10 text-white/55"}`}>{counts[x.id]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title={lang === "bg" ? "Няма резервации" : "No bookings"}
            description={lang === "bg" ? "Открий треньор и резервирай първата си сесия." : "Discover a coach and book your first session."}
            cta={lang === "bg" ? "Открий треньори" : "Discover coaches"}
            ctaTo="/find-coaches"
            testId="empty-bookings"
          />
        ) : (
          <div className="space-y-3 reveal-stagger">
            {filtered.map((b) => {
              const sl = STATUS_META[b.status] || STATUS_META.pending_payment;
              const slotPast = b.slot_at && new Date(b.slot_at) < new Date();
              const isCoach  = user?.role === "coach";
              const isParent = user?.role === "parent";
              const isCash   = b.payment_method === "cash";
              const canConfirm  = isCoach && ["paid", "pending_cash"].includes(b.status);
              const canMarkPaid = isCoach && isCash && ["pending_cash", "confirmed"].includes(b.status);
              const canReject   = isCoach && ["pending_payment", "pending_cash", "paid"].includes(b.status);
              const canCancel   = ["pending_payment", "pending_cash", "paid", "confirmed"].includes(b.status) && !slotPast;
              const canComplete = isCoach && b.status === "confirmed" && slotPast;
              return (
                <div key={b.id} className="card-coach p-5 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between" data-testid={`booking-${b.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <Avatar name={b.other_user?.name} src={b.other_user?.photo_url} size="lg" />
                    <div className="min-w-0">
                      <div className="font-oswald uppercase text-xl tracking-tight truncate">{b.other_user?.name}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/55 mt-1 font-mono">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-pitch" /> {dateFmt(b.slot_at)}</span>
                        <span>{b.duration_min}{lang === "bg" ? "мин" : "min"}</span>
                        {b.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-pitch" /> {b.location}</span>}
                      </div>
                      {b.notes && <div className="text-xs text-white/40 mt-1 max-w-md line-clamp-2">{b.notes}</div>}
                      {b.rejection_reason && <div className="text-xs text-amber-400/85 mt-1 italic">↳ {b.rejection_reason}</div>}
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:flex-shrink-0">
                    <div className="text-right md:text-left">
                      <div className="font-oswald text-2xl text-pitch text-glow-emerald leading-none">{(b.currency || "eur").toLowerCase() === "usd" ? "$" : "€"}{b.amount}</div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className={`chip-badge ${sl.chip}`} data-testid="booking-status-badge">{lang === "bg" ? sl.bg : sl.en}</span>
                        {isCash && <span className="chip-badge" data-testid="booking-cash-tag">💵 {lang === "bg" ? "Кеш" : "Cash"}</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {canConfirm && (
                        <button onClick={() => action(b, "confirm")} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`confirm-${b.id}`}>
                          <Check className="w-3 h-3" /> {lang === "bg" ? "Потвърди" : "Confirm"}
                        </button>
                      )}
                      {canMarkPaid && (
                        <button onClick={() => action(b, "mark-paid")} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`mark-paid-${b.id}`}>
                          💵 {lang === "bg" ? "Маркирай платено" : "Mark paid"}
                        </button>
                      )}
                      {canComplete && (
                        <button onClick={() => action(b, "complete")} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`complete-${b.id}`}>
                          <CheckCheck className="w-3 h-3" /> {lang === "bg" ? "Завърши" : "Complete"}
                        </button>
                      )}
                      {canReject && (
                        <button onClick={() => action(b, "reject")} className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid={`reject-${b.id}`}>
                          <X className="w-3 h-3" /> {lang === "bg" ? "Откажи" : "Reject"}
                        </button>
                      )}
                      {canCancel && b.status !== "pending_payment" && (
                        <button onClick={() => action(b, "reschedule")} className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`reschedule-${b.id}`}>
                          {lang === "bg" ? "Пренасрочи" : "Reschedule"}
                        </button>
                      )}
                      {b.refund_status && b.refund_status !== "not_applicable" && <span className="text-[10px] font-mono uppercase text-white/50" data-testid={`refund-status-${b.id}`}>{b.refund_status.replace("_", " ")}</span>}
                      {canCancel && (
                        <button onClick={() => action(b, "cancel")} className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`cancel-${b.id}`}>
                          <Ban className="w-3 h-3" /> {lang === "bg" ? "Откажи" : "Cancel"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
